import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseSpeechRecognitionOptions {
  lang?: string
  continuous?: boolean
  interimResults?: boolean
  onFinalTranscript?: (transcript: string) => void
}

export interface UseSpeechRecognitionResult {
  start: () => void
  stop: () => void
  abort: () => void
  isListening: boolean
  isSupported: boolean
  transcript: string
  interim: string
  error: string | null
}

function getRecognitionCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null
}

function humanizeError(code: string): string {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Permission micro refusée. Active-la dans les réglages du navigateur.'
    case 'no-speech':
      return 'Rien entendu. Réessaie en parlant plus fort.'
    case 'audio-capture':
      return 'Aucun micro détecté.'
    case 'network':
      return 'Erreur réseau pendant la reconnaissance vocale.'
    case 'language-not-supported':
      return 'Langue non supportée par le navigateur.'
    case 'aborted':
      return ''
    default:
      return `Erreur micro: ${code}`
  }
}

export function useSpeechRecognition(
  options: UseSpeechRecognitionOptions = {},
): UseSpeechRecognitionResult {
  const { lang = 'fr-FR', continuous = false, interimResults = true, onFinalTranscript } = options

  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [interim, setInterim] = useState('')
  const [error, setError] = useState<string | null>(null)

  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const finalRef = useRef('')
  // Latest callback ref so we don't have to recreate the recognition instance
  // every time the parent passes a new closure.
  const onFinalRef = useRef<((transcript: string) => void) | undefined>(onFinalTranscript)

  useEffect(() => {
    onFinalRef.current = onFinalTranscript
  }, [onFinalTranscript])

  const Ctor = getRecognitionCtor()
  const isSupported = Ctor !== null

  useEffect(() => {
    if (!Ctor) return undefined

    const recognition = new Ctor()
    recognition.lang = lang
    recognition.continuous = continuous
    recognition.interimResults = interimResults
    recognition.maxAlternatives = 1

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimText = ''
      let finalText = finalRef.current
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i]
        if (!result) continue
        const alt = result[0]
        if (!alt) continue
        if (result.isFinal) {
          finalText += alt.transcript
        } else {
          interimText += alt.transcript
        }
      }
      finalRef.current = finalText
      setTranscript(finalText)
      setInterim(interimText)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const message = humanizeError(event.error)
      if (message) setError(message)
    }

    recognition.onend = () => {
      setIsListening(false)
      setInterim('')
      const final = finalRef.current.trim()
      if (final) {
        onFinalRef.current?.(final)
      }
    }

    recognition.onstart = () => {
      setIsListening(true)
      setError(null)
    }

    recognitionRef.current = recognition

    return () => {
      recognition.onresult = null
      recognition.onerror = null
      recognition.onend = null
      recognition.onstart = null
      try {
        recognition.abort()
      } catch {
        // ignore double-abort
      }
      recognitionRef.current = null
    }
  }, [Ctor, lang, continuous, interimResults])

  const start = useCallback(() => {
    const recognition = recognitionRef.current
    if (!recognition) return
    finalRef.current = ''
    setTranscript('')
    setInterim('')
    setError(null)
    try {
      recognition.start()
    } catch (err) {
      // Calling start() twice throws InvalidStateError; surface only the unexpected ones.
      const name = (err as Error).name
      if (name !== 'InvalidStateError') {
        setError((err as Error).message)
      }
    }
  }, [])

  const stop = useCallback(() => {
    recognitionRef.current?.stop()
  }, [])

  const abort = useCallback(() => {
    finalRef.current = ''
    setTranscript('')
    setInterim('')
    recognitionRef.current?.abort()
  }, [])

  return { start, stop, abort, isListening, isSupported, transcript, interim, error }
}
